import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pprApi, type PprContact, type PprJob } from '@/lib/apiClient';
import { fmtDate, daysSince } from '../pprShared';
import EmailGeneratorModal from '../EmailGeneratorModal';

/**
 * Contacts & Notifications tab.
 *
 * Lists the nine PPR contacts for the job. For each contact:
 *   • Show current notification + response state.
 *   • Inline-edit name and email (gated by canEdit).
 *   • Mark Notified / Mark Responded buttons (gated by canEdit).
 *
 * Reminder + escalation automation is live (Power Automate polls
 * /ppr/automation/due daily) — `reminderCount`, `lastReminderDate` and
 * `escalated` are written by it and are read-only here; coordinators don't flip
 * them by hand.
 *
 * ⚠️ `escalated` stays set for good once the coordinator has been emailed about
 * a contact — it is what stops the same contact escalating twice — so it is NOT
 * on its own a statement about the row's current state. Anything the coordinator
 * reads as outstanding work is gated on `!responseReceived` as well, the same
 * way the overdue warning below is. Without that, a contact who answered on day
 * six kept a red border and a ⚠ Escalated tag for the life of the review.
 */
export default function ContactsTab({
  job,
  canEdit,
}: {
  job: PprJob;
  canEdit: boolean;
}) {
  const contacts = job.contacts ?? [];

  return (
    // Deliberately unclassed: the rows below carry their own card chrome, so
    // this is a plain block wrapper. It used to say `ppr-contacts`, which is
    // declared only in StartPprModal — a different, border-bottom separated
    // table on the sibling dashboard route, so nothing ever rendered from it
    // here. Restoring that rule would have added a wrong outer frame around
    // the feedback card and the already-bordered rows.
    <div>
      <TeamFeedbackCard job={job} canEdit={canEdit} />
      <p className="ppr-contacts-summary">
        <strong>{job.contactsResponded ?? 0} of {job.contactsTotal ?? (contacts.length || 9)}</strong> responses received
        {(job.contactsOverdue ?? 0) > 0 && (
          <span className="ppr-contacts-overdue"> · {job.contactsOverdue} overdue</span>
        )}
        {(job.contactsEscalated ?? 0) > 0 && (
          <span className="ppr-contacts-escalated"> · {job.contactsEscalated} escalated</span>
        )}
      </p>

      <div className="ppr-contact-list">
        {contacts.length === 0 && (
          <p className="ppr-empty">No contacts on this PPR. (This shouldn't happen — every PPR starts with 9 contacts.)</p>
        )}
        {contacts.map((c) => (
          <ContactRow key={c.id} job={job} contact={c} canEdit={canEdit} />
        ))}
      </div>
    </div>
  );
}

function ContactRow({
  job,
  contact,
  canEdit,
}: {
  job: PprJob;
  contact: PprContact;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['ppr', 'job', job.id] });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact.contactName ?? '');
  const [email, setEmail] = useState(contact.contactEmail ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PprContact>) => pprApi.updateContact(job.id, contact.id, data),
    onSuccess: () => {
      setEditing(false);
      setSaveError(null);
      invalidate();
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  const notifyMutation = useMutation({
    mutationFn: () => pprApi.markNotified(job.id, contact.id),
    onSuccess: invalidate,
    onError: (err: Error) => setSaveError(err.message),
  });

  const respondMutation = useMutation({
    mutationFn: () => pprApi.markResponded(job.id, contact.id),
    onSuccess: invalidate,
    onError: (err: Error) => setSaveError(err.message),
  });

  // Undo a mistaken flip (e.g. a webhook routed to the wrong position).
  const unmarkRespondedMutation = useMutation({
    mutationFn: () =>
      pprApi.updateContact(job.id, contact.id, {
        responseReceived: false,
        responseDate: null,
      } as Partial<PprContact>),
    onSuccess: invalidate,
    onError: (err: Error) => setSaveError(err.message),
  });

  const unmarkNotifiedMutation = useMutation({
    mutationFn: () =>
      pprApi.updateContact(job.id, contact.id, {
        notificationSent: false,
        notificationDate: null,
      } as Partial<PprContact>),
    onSuccess: invalidate,
    onError: (err: Error) => setSaveError(err.message),
  });

  const daysSinceNotified = contact.notificationSent ? daysSince(contact.notificationDate) : null;
  const isOverdue =
    contact.notificationSent && !contact.responseReceived && (daysSinceNotified ?? 0) > 3;
  // Escalated AND still unanswered. The flag alone is history, not a live state
  // — see the note at the top of the file.
  const isEscalated = contact.escalated && !contact.responseReceived;

  return (
    <div
      className={`ppr-contact-row${isEscalated ? ' ppr-contact-row-escalated' : isOverdue ? ' ppr-contact-row-overdue' : ''}`}
    >
      <div className="ppr-contact-position">{contact.position}</div>

      <div className="ppr-contact-identity">
        {editing ? (
          <>
            <input
              className="ppr-input"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="ppr-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="ppr-contact-edit-actions">
              <button
                className="btn btn-primary btn-sm"
                disabled={updateMutation.isPending}
                onClick={() =>
                  updateMutation.mutate({
                    contactName: name.trim() || null,
                    contactEmail: email.trim() || null,
                  } as Partial<PprContact>)
                }
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setEditing(false);
                  setName(contact.contactName ?? '');
                  setEmail(contact.contactEmail ?? '');
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="ppr-contact-name">
              {contact.contactName || <span className="ppr-muted">No name set</span>}
            </div>
            <div className="ppr-contact-email">
              {contact.contactEmail || <span className="ppr-muted">No email set</span>}
            </div>
            {canEdit && (
              <button
                className="ppr-linkbtn"
                onClick={() => setEditing(true)}
                aria-label={`Edit ${contact.position}`}
              >
                Edit
              </button>
            )}
          </>
        )}
      </div>

      <div className="ppr-contact-state">
        <div className="ppr-contact-state-line">
          <Dot state={contact.notificationSent ? 'on' : 'off'} />
          <span>
            Notified{contact.notificationSent && contact.notificationDate ? `: ${fmtDate(contact.notificationDate)}` : ''}
          </span>
        </div>
        <div className="ppr-contact-state-line">
          <Dot state={contact.responseReceived ? 'on' : 'off'} />
          <span>
            Responded{contact.responseReceived && contact.responseDate ? `: ${fmtDate(contact.responseDate)}` : ''}
          </span>
        </div>
        {isOverdue && !isEscalated && (
          <div className="ppr-contact-warning">
            ⚠ {daysSinceNotified} days since notification, no response
          </div>
        )}
        {isEscalated && (
          <div className="ppr-contact-warning">
            ⚠ Escalated{contact.lastReminderDate ? ` (last reminder ${fmtDate(contact.lastReminderDate)})` : ''}
          </div>
        )}
        {contact.reminderCount > 0 && (
          <div className="ppr-contact-meta">{contact.reminderCount} reminder{contact.reminderCount === 1 ? '' : 's'} sent</div>
        )}
      </div>

      {canEdit && !editing && (
        <div className="ppr-contact-actions">
          <button className="btn btn-sm" onClick={() => setEmailOpen(true)}>
            ✉ Generate Email
          </button>
          {!contact.notificationSent && (
            <button
              className="btn btn-sm"
              disabled={notifyMutation.isPending}
              onClick={() => notifyMutation.mutate()}
            >
              {notifyMutation.isPending ? '…' : 'Mark Notified'}
            </button>
          )}
          {contact.notificationSent && !contact.responseReceived && (
            <button
              className="btn btn-primary btn-sm"
              disabled={respondMutation.isPending}
              onClick={() => respondMutation.mutate()}
            >
              {respondMutation.isPending ? '…' : 'Mark Responded'}
            </button>
          )}
          {contact.responseReceived && (
            <button
              className="ppr-linkbtn"
              disabled={unmarkRespondedMutation.isPending}
              onClick={() => unmarkRespondedMutation.mutate()}
            >
              {unmarkRespondedMutation.isPending ? '…' : 'Unmark responded'}
            </button>
          )}
          {contact.notificationSent && !contact.responseReceived && (
            <button
              className="ppr-linkbtn"
              disabled={unmarkNotifiedMutation.isPending}
              onClick={() => unmarkNotifiedMutation.mutate()}
            >
              {unmarkNotifiedMutation.isPending ? '…' : 'Unmark notified'}
            </button>
          )}
        </div>
      )}

      {saveError && <div className="ppr-contact-error">{saveError}</div>}

      {emailOpen && (
        <EmailGeneratorModal job={job} contact={contact} onClose={() => setEmailOpen(false)} />
      )}
    </div>
  );
}

function Dot({ state }: { state: 'on' | 'off' }) {
  return (
    <span
      className={`ppr-dot ${state === 'on' ? 'ppr-dot-on' : 'ppr-dot-off'}`}
      aria-hidden
    />
  );
}

/**
 * Field-leader feedback on their crew.
 *
 * ⚠️ This is NOT the Field Leader contact's survey response below. That response
 * is the reply to the "Field Team Feedback Needed" email; the product owner confirmed
 * (2026-07-29) the feedback on the crew is a separate deliverable, so it's tracked
 * on its own and deliberately sits ABOVE the nine contacts rather than inside
 * them — the whole point is that it can be outstanding while the survey response
 * is in, and vice versa.
 *
 * Placed on this tab because the field leader is here; the distinction is easier
 * to see next to their contact row than on a tab of its own.
 */
function TeamFeedbackCard({ job, canEdit }: { job: PprJob; canEdit: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(job.teamFeedbackNotes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (complete: boolean) => pprApi.updateJob(job.id, {
      teamFeedbackComplete: complete,
      // Stamp today ONLY on the incomplete → complete transition. Editing the
      // notes on an already-complete item re-saves with complete === true, so
      // stamping unconditionally would silently move the completion date to today
      // every time someone tidied the wording — losing when it actually happened.
      // Clearing to null when un-completing stays correct: a stale date must not
      // outlive the claim.
      teamFeedbackDate: complete
        ? (job.teamFeedbackComplete ? job.teamFeedbackDate : new Date().toISOString().slice(0, 10))
        : null,
      teamFeedbackNotes: notes.trim() === '' ? null : notes.trim(),
    }),
    onSuccess: () => { setEditing(false); setError(null); qc.invalidateQueries({ queryKey: ['ppr'] }); },
    onError: (e: Error) => setError(e.message),
  });

  const done = job.teamFeedbackComplete;

  return (
    <div style={{
      border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', background: '#fff',
      padding: '11px 14px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: 'var(--ew-fw-semibold) 12px/1.3 var(--ew-font)', color: 'var(--ew-dark)' }}>
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: 999, marginRight: 7,
              background: done ? 'var(--ew-green)' : 'var(--ew-n-300, #C9C9C6)',
            }} />
            Field leader feedback on their team
          </div>
          <div style={{ font: 'var(--ew-fw-regular) 11.5px/1.45 var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 3 }}>
            {done
              ? `Completed${job.teamFeedbackDate ? ` ${fmtDate(job.teamFeedbackDate)}` : ''}.`
              : 'Not yet completed — separate from the field leader’s survey response below.'}
          </div>
        </div>
        {canEdit && !editing && (
          <button
            onClick={() => { setNotes(job.teamFeedbackNotes ?? ''); setEditing(true); }}
            style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'var(--ew-quality)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
          >
            {done ? 'Edit' : 'Record it'}
          </button>
        )}
      </div>

      {!editing && job.teamFeedbackNotes && (
        <div style={{ marginTop: 8, font: 'var(--ew-fw-regular) 12.5px/1.5 var(--ew-font)', color: 'var(--ew-dark)', whiteSpace: 'pre-wrap' }}>
          {job.teamFeedbackNotes}
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 10 }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="What the field leader said about the crew — who stood out, who needs support, anything for the next job."
            style={{
              width: '100%', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
              padding: '7px 9px', font: 'var(--ew-fw-regular) 12.5px/1.45 var(--ew-font)',
              background: '#fff', color: 'var(--ew-dark)', resize: 'vertical',
            }}
          />
          {error && (
            <div style={{ marginTop: 6, padding: '6px 9px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
            <button onClick={() => setEditing(false)} disabled={save.isPending}
                    style={{ border: '1px solid var(--ew-n-200)', background: '#fff', borderRadius: 6, padding: '5px 11px', font: 'var(--ew-fw-medium) 12px var(--ew-font)', cursor: 'pointer' }}>
              Cancel
            </button>
            {done && (
              <button onClick={() => save.mutate(false)} disabled={save.isPending}
                      style={{ border: '1px solid var(--ew-n-200)', background: '#fff', borderRadius: 6, padding: '5px 11px', font: 'var(--ew-fw-medium) 12px var(--ew-font)', cursor: 'pointer' }}>
                Mark not completed
              </button>
            )}
            <button onClick={() => save.mutate(true)} disabled={save.isPending}
                    style={{ border: '1px solid var(--ew-green)', background: 'var(--ew-green)', color: '#fff', borderRadius: 6, padding: '5px 11px', font: 'var(--ew-fw-medium) 12px var(--ew-font)', cursor: 'pointer' }}>
              {save.isPending ? 'Saving…' : done ? 'Save' : 'Mark completed'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
